



































import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import VuePlotly from "@statnett/vue-plotly";

import { mixins } from "vue-class-component";
import QueryEndpointData from "./dataquery-mixin";
import { eventLoading } from "@/components/sce/EventLoading";

// Sequential blue→purple ramp keyed by crossings count. Ordinal:
// 0 = lightest (intrastate, "best case"), higher counts ramp toward
// dark purple.
const BORDER_CROSSING_COLORS = [
    "#64b5f6", // 0 — light blue
    "#1e88e5", // 1 — medium blue
    "#3949ab", // 2 — indigo
    "#5e35b1", // 3 — purple
    "#4a148c", // 4+ — dark purple
];
const BORDER_CROSSING_FALLBACK = "#4a148c";

@Component({
    components: {
        VuePlotly,
    },
})
export default class BorderCrossingsBarChart extends mixins(QueryEndpointData) {
    @Prop() protected config!: any;

    // This is only temporary... will need to hit an endpoint which runs the propogation model eventually.
    get bordercrossingsUrl() {
        return (
            process.env.VUE_APP_BACKEND_BASE +
            "/supplychainexplorer/bordercrossings"
        );
    }

    /** True whenever ANY component on the dashboard has an in-flight
     *  fetch (driven by the shared `EventLoading` bus's loadingCount).
     *  Used to ignore segment clicks while the dashboard is
     *  re-loading — otherwise a click here triggers a filter change
     *  that races with the still-in-progress reload. */
    isDashboardLoading: boolean = false;

    mounted() {
        // Click handling stays on the template's `@click` binding —
        // don't move it to `$refs.borderCrossingsChart` here. The root
        // `v-if="data.length"` means the chart isn't mounted yet, so
        // refs are undefined at this point.
        this.loadData(this.bordercrossingsUrl);
    }

    created() {
        eventLoading.$on("loading", this.setDashboardLoading);
    }

    beforeDestroy() {
        eventLoading.$off("loading", this.setDashboardLoading);
    }

    setDashboardLoading(isLoading: boolean) {
        this.isDashboardLoading = isLoading;
    }

    @Watch("updateRequired")
    reloadData(newVal: any, oldVal: any) {
        this.loadData(this.bordercrossingsUrl);
    }

    get crossingsData(): [any] {
        // Get the plotly data object for State or Territory
        // crossings for a horizontal bar chart display.

        return this.data.map((value: any) => {
            // Get a custom label
            let label = String(value.state_territory_border_crossings);
            if (value.state_territory_border_crossings === 0) {
                label = "Intrastate";
            } else if (value.state_territory_border_crossings === 1) {
                label = "Neighbouring";
            } else {
                label = "Multi-Border";
            }

            const crossings = value.state_territory_border_crossings;
            const color =
                BORDER_CROSSING_COLORS[crossings] ?? BORDER_CROSSING_FALLBACK;

            return {
                type: "bar",
                orientation: "h",
                name: value.state_territory_border_crossings,
                y: ["State or Territory<br>Border Crossings"],
                x: [value.tonnes * 100],
                marker: {
                    color,
                    opacity: 0.85,
                },
                text: [`${(value.tonnes * 100).toFixed(1)}%`], // Value inside the bar
                textposition: "inside", // Places text inside the bar
                insidetextanchor: "middle", // Aligns the value to the center
                textfont: {
                    color: "white", // Force text color to white (it can render black on light backgrounds)
                },
                customdata: [[label, this.config]],
                hovertemplate: `%{customdata[0]}: %{x:.1f}% of total tonnes<extra></extra>`,
            };
        });
    }

    get annotations(): [any] {
        // Count-label annotations centred over each segment. y=0.63 sits
        // above the bar.
        var total = 0;
        return this.data.map((value: any) => {
            var percentage = value.tonnes * 100;
            if (percentage < 1) return null;
            total = total + percentage;
            const crossings = value.state_territory_border_crossings;
            // Append "crossing(s)" only when the segment is wide enough
            // to fit the word; otherwise fall back to just the number so
            // the label doesn't overflow into adjacent segments.
            const word = crossings === 1 ? "crossing" : "crossings";
            const text =
                percentage >= 18 ? `${crossings} ${word}` : `${crossings}`;
            return {
                x: total - percentage / 2,
                y: 0.63,
                text,
                showarrow: false,
            };
        });
    }

    get crossingsLayout(): any {
        // Get the plotly layout object for State or Territory
        // crossings for a horizontal bar char display.

        return {
            barmode: "stack",
            showlegend: false,
            bargap: 0.15,
            margin: { l: 10, r: 10, b: 0, t: 0 },
            font: {
                size: 14,
            },
            xaxis: {
                showgrid: false, // Removes grid lines
                zeroline: false, // Removes the zero line
                range: [0, 100],
            },
            yaxis: {
                showgrid: false,
                zeroline: false,
                showticklabels: false,
                automargin: false,
            },
            height: 60,
            autosize: true,
            dragmode: false,
            hovermode: "closest",
            annotations: this.annotations,
        };
    }

    handleBorderCrossingClick(event: any) {
        if (this.isDashboardLoading) return;
        var point = event.points[0];
        var map_id = point.data.customdata[0][1].map_id;
        var clickedValue = point.data.name; // This is state_territory_border_crossings number (i.e.: 0,1,2,3)

        this.$emit("crossing-selected", { clickedValue, map_id });
    }
}
